<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

defineOptions({
  name: 'dataCountries',
});

const store = useStore<RootState>();

// Базовый тип для сообщений с ID страны
type CountryWithId = {
  idCountry: number;
};

// Базовый тип для общих полей страны
type CountryCommonFields = {
  code: string;
  currencySymbol: string;
};

// Типы для WebSocket сообщений компонента countries
type CountryRootMessage = WebSocketMessageWithFields<'language-country-root' | 'language-country-name' | 'profile-main' | 'profile-locale-details', {
  country: unknown;
}>;
type CountryRootNotFoundMessage = WebSocketMessageEmpty<'language-country-root-not-found'>;
type CountriesListMessage = WebSocketMessageWithFields<'language-country-list' | 'language-locale-list' | 'language-switcher-data', {
  countries: unknown[];
}>;
type CountryCreateMessage = WebSocketMessageWithFields<'language-country-create' | 'language-country-create-success', CountryWithId & CountryCommonFields>;
type CountryUpdateMessage = WebSocketMessageWithFields<'language-country-update' | 'language-country-update-success', CountryWithId & CountryCommonFields & {
  idLocaleDefault: number;
}>;
type CountryDeleteMessage = WebSocketMessageWithFields<'language-country-delete' | 'language-country-delete-success', CountryWithId>;
type CountryLockMessage = WebSocketMessageWithFields<'language-country-lock' | 'language-country-lock-success', CountryWithId>;
type CountryUnlockMessage = WebSocketMessageWithFields<'language-country-unlock' | 'language-country-unlock-success', CountryWithId>;

// Объединенный тип для всех сообщений
type CountriesWebSocketMessage = 
  | CountryRootMessage 
  | CountryRootNotFoundMessage 
  | CountriesListMessage 
  | CountryCreateMessage 
  | CountryUpdateMessage 
  | CountryDeleteMessage 
  | CountryLockMessage 
  | CountryUnlockMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: CountriesWebSocketMessage): void => {
  if (!data?.signal) return;
  switch (data.signal) {
    case 'language-country-root':
    case 'language-country-name':
    case 'profile-main':
    case 'profile-locale-details':
      store.dispatch('country/wsSetCountry', data.country);
      break;
    case 'language-country-root-not-found':
      store.dispatch('country/wsSetEmptyCountry');
      break;
    case 'language-country-list':
    case 'language-locale-list':
    case 'language-switcher-data':
      store.dispatch('countries/wsSetCountries', { countries: data.countries });
      break;
    case 'language-country-create':
    case 'language-country-create-success':
      store.dispatch('countries/wsCountryCreate', data);
      break;
    case 'language-country-update':
    case 'language-country-update-success':
      store.dispatch('countries/wsCountryUpdate', data);
      break;
    case 'language-country-delete':
    case 'language-country-delete-success':
      store.dispatch('countries/wsCountryDelete', { idCountry: data.idCountry });
      break;
    case 'language-country-lock':
    case 'language-country-lock-success':
      store.dispatch('countries/wsCountryLock', { idCountry: data.idCountry });
      break;
    case 'language-country-unlock':
    case 'language-country-unlock-success':
      store.dispatch('countries/wsCountryUnlock', { idCountry: data.idCountry });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
